import React, { useEffect, useState, useCallback } from 'react';
import '../admin.css';
import { Card, Tag, Space, Button, Modal, Typography, Form, Input, Popconfirm, message, Empty, Select, Table } from 'antd';
import { API_BASE, authHeaders } from '../../config/api';
import { EyeOutlined, LinkOutlined } from '@ant-design/icons';

const industryOptions = [
  { value: 'E-commerce', label: 'E-commerce websites' },
  { value: 'Non-profit organization', label: 'Non-profit organization websites' },
  { value: 'SaaS product / web app', label: 'SaaS product websites / web apps' },
  { value: 'Insurance company', label: 'Insurance company websites' },
  { value: 'Digital marketing agency', label: 'Digital marketing agency websites' },
  { value: 'SME or small business', label: 'SME or small business websites (non-technical owners)' },
  { value: 'University or educational institution', label: 'University or educational institution websites' },
  { value: 'Other', label: 'Other (please specify)' }
];

const { Title } = Typography;

const ManageWebsites = () => {
  // API helpers - using centralized config
  const apiBase = API_BASE;

  // State
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // [{id, url, status, created_at, source}]
  const [snippets, setSnippets] = useState([]);
  const [snippetModal, setSnippetModal] = useState({ open: false, code: '', url: '' });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // website record
  const [addingForm] = Form.useForm();
  const selectedIndustry = Form.useWatch?.('industry', addingForm);
  const [editingForm] = Form.useForm();

  const columns = [
    {
      title: 'Website',
      dataIndex: 'url',
      key: 'website',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1f2937' }}>
            {(rec.url || '').replace('https://', '').replace('http://', '')}
          </div>
          {rec.industry && (
            <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2, fontWeight: 500 }}>
              {rec.industry}
            </div>
          )}
          {rec.created_at && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {`Added ${new Date(rec.created_at).toLocaleString()}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tag',
      dataIndex: 'source',
      key: 'tag',
      render: (_, rec) => (
        rec.source === 'business_account' ? (
          <Tag color="blue" style={{ margin: 0 }}>Business</Tag>
        ) : (
          <Tag color="purple" style={{ margin: 0 }}>Managed</Tag>
        )
      ),
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      render: (industry) => (
        <span style={{ fontSize: 12, color: '#334155' }}>
          {industry || '—'}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={status === 'active' ? 'green' : status === 'inactive' ? 'red' : status === 'pending' ? 'gold' : 'default'}
          style={{ margin: 0 }}
        >
          {status || 'unknown'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, rec) => (
        <Space>
          <Button size="small" icon={<LinkOutlined />} onClick={() => goTo(rec.url)}>Visit Site</Button>
          <Button size="small" icon={<EyeOutlined />} onClick={() => {
            const match = snippets.find(s => String(s.website_id) === String(rec.id));
            setSnippetModal({ open: true, code: match?.snippet_code || '// No snippet found for this website.', url: rec.url });
          }}>View Snippet</Button>
          <Button size="small" type="default" onClick={() => openEdit(rec)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete website"
            description={`Remove ${rec.url}?`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(rec)}
          >
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch managed websites and business account in parallel
      const [resWeb, resBA] = await Promise.all([
        fetch(`${apiBase}/my/websites`, { headers: { ...authHeaders() } }),
        fetch(`${apiBase}/my/business-account`, { headers: { ...authHeaders() } })
      ]);
      const bodyWeb = await resWeb.json().catch(() => ({}));
      const bodyBA = await resBA.json().catch(() => (null));
      const webRows = Array.isArray(bodyWeb?.items) ? bodyWeb.items : Array.isArray(bodyWeb) ? bodyWeb : [];
      // Also load snippets for view-snippet feature
      const sRes = await fetch(`${apiBase}/my/snippets`, { headers: { ...authHeaders() } });
      const sBody = await sRes.json().catch(() => ({}));
      setSnippets(Array.isArray(sBody?.items) ? sBody.items : Array.isArray(sBody) ? sBody : []);

      // Optionally include business account website as a read-only card
      const extra = [];
      if (bodyBA && bodyBA.website) {
        extra.push({
          id: `ba-${bodyBA.id}`,
          url: bodyBA.website,
          status: 'active',
          created_at: bodyBA.created_at || null,
          source: 'business_account'
        });
      }
      // Avoid duplicates by normalized host
      const normalize = (u) => String(u || '').replace('https://','').replace('http://','').replace(/\/$/,'');
      const seen = new Set();
      const merged = [...webRows.map(r => ({ ...r, source: r.source || 'websites_table' })), ...extra]
        .filter(r => {
          const key = normalize(r.url);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      setItems(merged);
    } catch (e) {
      message.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  const normalizeUrl = (u) => {
    const s = String(u || '').trim();
    if (!s) return '';
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    return `https://${s}`;
  };

  const goTo = (u) => {
    const url = normalizeUrl(u);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAdd = async () => {
    try {
      const vals = await addingForm.validateFields();
      const url = normalizeUrl(vals.url);
      const industry = vals.industry === 'Other'
        ? (vals.customIndustry || 'Other')
        : vals.industry;
      const res = await fetch(`${apiBase}/my/websites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ url, status: 'active', industry })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to add website');
      message.success('Website added');
      setAddOpen(false);
      addingForm.resetFields();
      await load();
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error(e.message || 'Failed to add website');
    }
  };

  const openEdit = (rec) => {
    setEditing(rec);
    editingForm.setFieldsValue({
      url: (rec.url || '').replace('https://','').replace('http://',''),
      status: rec.status || 'active'
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    try {
      if (!editing) return;
      const vals = await editingForm.validateFields();
      const url = normalizeUrl(vals.url);
      if (editing?.source === 'business_account') {
        // Update the business account's website field
        const res = await fetch(`${apiBase}/my/business-account`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ website: url })
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to update business website');
      } else {
        const status = vals.status;
        const res = await fetch(`${apiBase}/my/websites/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ url, status })
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to update website');
      }
      message.success('Website updated');
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to update website');
    }
  };

  const handleDelete = async (rec) => {
    try {
      if (rec?.source === 'business_account') {
        // Clear business account website
        const res = await fetch(`${apiBase}/my/business-account`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ website: null })
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to remove business website');
      } else {
        const res = await fetch(`${apiBase}/my/websites/${rec.id}` , {
          method: 'DELETE',
          headers: { ...authHeaders() }
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to delete website');
        }
      }
      message.success('Website deleted');
      await load();
    } catch (e) {
      message.error(e.message || 'Failed to delete website');
    }
  };

  return (
    <div className="abo-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Manage Websites</Title>
          <div style={{ color: '#64748b', fontSize: 13 }}>View, add, edit and remove your websites</div>
        </div>
        <Space>
          <Button onClick={load} loading={loading}>Refresh</Button>
          <Button type="primary" onClick={() => setAddOpen(true)}>Add Website</Button>
        </Space>
      </div>

      {/* Websites table */}
      <Card hoverable style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }} loading={loading}>
        {(!items || items.length === 0) ? (
          <Empty description="No websites yet" />
        ) : (
          <Table
            columns={columns}
            dataSource={items}
            rowKey={(r) => r.id}
            pagination={false}
          />
        )}
      </Card>

      {/* Add modal */}
      <Modal
        title="Add Website"
        open={addOpen}
        onOk={handleAdd}
        onCancel={() => setAddOpen(false)}
        okText="Add"
      >
        <Form layout="vertical" form={addingForm} requiredMark={false}>
          <Form.Item
            label="Website Type / Industry"
            name="industry"
            rules={[{ required: true, message: 'Please select your website type/industry' }]}
            extra="This helps provide insights tailored to your industry"
          >
            <Select
              placeholder="Select your website type"
              options={industryOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          {selectedIndustry === 'Other' && (
            <Form.Item
              label="Describe your industry"
              name="customIndustry"
              rules={[{ required: true, message: 'Please enter your industry' }]}
            >
              <Input placeholder="e.g., Real Estate, Hospitality, Finance" allowClear />
            </Form.Item>
          )}
          <Form.Item
            label="Domain or URL"
            name="url"
            rules={[
              { required: true, message: 'Please enter a domain or full URL' },
              { pattern: /^[a-zA-Z0-9.:/-]+$/, message: 'Invalid characters' }
            ]}
          >
            <Input placeholder="example.com or https://example.com" allowClear disabled={!selectedIndustry} />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Snippet Modal */}
      <Modal
        title={<span><EyeOutlined style={{ marginRight: 8 }} />Snippet — {snippetModal.url}</span>}
        open={snippetModal.open}
        onOk={() => setSnippetModal({ open: false, code: '', url: '' })}
        onCancel={() => setSnippetModal({ open: false, code: '', url: '' })}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={760}
      >
        <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 14, borderRadius: 8, marginTop: 12, overflow: 'auto', maxHeight: 420, fontSize: 12, lineHeight: 1.5 }}>
          {snippetModal.code}
        </pre>
      </Modal>

      {/* Edit modal */}
      <Modal
        title="Edit Website"
        open={editOpen}
        onOk={handleEdit}
        onCancel={() => { setEditOpen(false); setEditing(null); }}
        okText="Save"
      >
        <Form layout="vertical" form={editingForm} requiredMark={false}>
          <Form.Item
            label="Domain or URL"
            name="url"
            rules={[
              { required: true, message: 'Please enter a domain or full URL' },
              { pattern: /^[a-zA-Z0-9.:/-]+$/, message: 'Invalid characters' }
            ]}
          >
            <Input placeholder="example.com or https://example.com" allowClear />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select
              options={[
                { value: 'active', label: 'active' },
                { value: 'inactive', label: 'inactive' },
                { value: 'pending', label: 'pending' },
                { value: 'suspended', label: 'suspended' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageWebsites;

